import { useEffect, useState } from "react";

import { getOrganizationBranch } from "services/organizationBranch/rest";
import { OrganizationBranchDto } from "services/organizationBranch/restModels";

export interface UseOrganizationBranchDetailsResult {
    branch: OrganizationBranchDto | null;
    loading: boolean;
    error: boolean;
    setBranch: (branch: OrganizationBranchDto) => void;
    refetch: () => void;
}

/*
* Тариф и реквизиты заведения. Кешируется на уровне модуля с рассылкой подписчикам, а не
* поштучно на каждый вызов: филиал читают четверо сразу — кнопка тарифа в шапке (она
* смонтирована всегда), страница тарифа, настройки меню и попап истёкшего тарифа. Без общего
* кеша список меню открывался двумя одинаковыми запросами, а оплата тарифа обновляла страницу,
* но не подпись в шапке.
*
* Рассылка нужна по той же причине, что и в useSession: данные не только читают, но и меняют
* из другого места — страница тарифа кладёт сюда новые реквизиты (`setBranch`) и перезапрашивает
* филиал после оплаты (`refetch`).
* */
let cache: OrganizationBranchDto | null = null;
let isLoaded = false;
let hasError = false;
let inFlight: Promise<void> | null = null;

// Поколение: ответ, стартовавший до refetch, в кеш не попадает (см. useSession).
let generation = 0;

const listeners = new Set<() => void>();

const publish = (branch: OrganizationBranchDto | null, error: boolean) => {
    cache = branch;
    hasError = error;
    isLoaded = true;
    listeners.forEach((listener) => listener());
};

const loadBranch = (): Promise<void> => {
    if (!inFlight) {
        const startedAt = generation;

        inFlight = getOrganizationBranch()
            .then((result) => {
                if (startedAt !== generation) return;

                inFlight = null;
                publish(result, !result);
            })
            .catch(() => {
                if (startedAt !== generation) return;

                inFlight = null;
                publish(null, true);
            });
    }

    return inFlight;
};

export const useOrganizationBranchDetails = (): UseOrganizationBranchDetailsResult => {
    const [, forceUpdate] = useState<number>(0);

    useEffect(() => {
        const listener = () => forceUpdate((value) => value + 1);

        listeners.add(listener);

        if (!isLoaded) loadBranch();

        return () => {
            listeners.delete(listener);
        };
    }, []);

    return {
        branch: cache,
        // Загрузкой считается только первая: перезапрос подменяет данные молча, иначе плашка
        // тарифа мигала бы на всех экранах сразу.
        loading: !isLoaded,
        error: hasError,
        setBranch: (branch: OrganizationBranchDto) => publish(branch, false),
        refetch: () => {
            generation += 1;
            inFlight = null;
            loadBranch();
        },
    };
};
